import { cn } from '@/shared/lib';
import { Markdown } from '@/modules/assistant/parts/markdown';
import { DraftReview } from '@/modules/assistant/parts/draft-review';
import {
  assistantAgentTurnClasses,
  assistantTurnAuthorClasses,
  assistantTurnClasses,
  assistantUserTurnClasses,
} from '@/modules/assistant/parts/styles';
import type { AssistantMessage } from '@/api/schemas/assistant';

export type MessageItemProps = {
  /** One turn. */
  message: AssistantMessage;
  /** The agent's name, from the route-aware context. */
  agentName: string;
};

/**
 * One turn in the conversation.
 *
 * The person's own words are rendered as TEXT — their line breaks are kept, and
 * nothing in them is interpreted. The agent's answer is rendered as Markdown,
 * because that is the format it was written in, and because the difference is
 * visible: an answer that lists three steps should look like three steps.
 *
 * A draft belongs to the turn that produced it, so it is rendered inside that
 * turn rather than as a separate notice.
 *
 * A TURN THAT IS ONLY A DRAFT HAS NO PROSE. The model answers a "create this"
 * request by calling the page's draft tool, and a tool call carries no text — so
 * the answer is the draft and the review sentence inside it. Rendering the empty
 * body would put an empty bubble under the agent's name, which reads as a broken
 * message rather than as a turn that has something to review.
 */
export function MessageItem({ message, agentName }: MessageItemProps) {
  const isPerson = message.role === 'user';
  const hasBody = message.content.trim() !== '';

  return (
    <article className={cn(assistantTurnClasses, isPerson ? 'items-end' : 'items-start')}>
      <p className={assistantTurnAuthorClasses}>{isPerson ? 'You' : agentName}</p>

      <div className={isPerson ? assistantUserTurnClasses : assistantAgentTurnClasses}>
        {isPerson ? (
          <p className="whitespace-pre-wrap">{message.content}</p>
        ) : hasBody ? (
          <Markdown text={message.content} />
        ) : null}

        {message.draft === null ? null : <DraftReview draft={message.draft} />}
      </div>
    </article>
  );
}
